import Button from '../button/button'

import { VscChevronRight, VscChromeClose, VscEllipsis } from 'react-icons/vsc'
import { useLocation, useNavigate } from 'react-router-dom'
import { GiTripleShells } from 'react-icons/gi'
import { useTranslation } from 'react-i18next'
import { useRef, useEffect, useState } from 'react'

import './tapbar.scss'

type TapbarPorps = {
  openTabs: Array<{ path: string, title: string  }>
  setOpenTabs: (value: Array<{ path: string, title: string  }>) => void
}

function Tapbar(props: Readonly<TapbarPorps>) {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate();
  const tapBarRef = useRef<HTMLDivElement>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after'>('before');

  // Horizontales Scrollen mit Mausrad ermöglichen
  useEffect(() => {
    const tapBarElement = tapBarRef.current;
    if (!tapBarElement) return;

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        tapBarElement.scrollLeft += e.deltaY;
      }
    };

    tapBarElement.addEventListener('wheel', handleWheel, { passive: false });
    return () => tapBarElement.removeEventListener('wheel', handleWheel);
  }, []);

  const openTab = (path: string) => {
    if(props.openTabs.find(tab => tab.path === path)) {
      navigate(path)
    }
  }

  const closeTab = async(path: string) => {
    const newopenTabs: Array<{ path: string, title: string }> = JSON.parse(JSON.stringify(props.openTabs))
    const index = newopenTabs.findIndex((tab) => tab.path === path)
    newopenTabs.splice(index, 1)
    props.setOpenTabs(newopenTabs)
    if(path === location.pathname) {
      navigate(newopenTabs[0].path)
    }
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);

      // Bestimme ob wir links oder rechts vom Tab sind
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const mouseX = e.clientX;
      const elementMiddle = rect.left + rect.width / 2;

      setDropPosition(mouseX < elementMiddle ? 'before' : 'after');
    }
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex !== null && draggedIndex !== index) {
      // Update position während des Dragging
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const mouseX = e.clientX;
      const elementMiddle = rect.left + rect.width / 2;

      setDropPosition(mouseX < elementMiddle ? 'before' : 'after');
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDragOverIndex(null);
      setDropPosition('before');
      return;
    }

    const newTabs = [...props.openTabs];
    const draggedTab = newTabs[draggedIndex];

    // Entferne den gezogenen Tab
    newTabs.splice(draggedIndex, 1);

    // Berechne die neue Insert-Position
    let insertIndex = dropIndex;

    // Wenn wir nach dem Element einfügen wollen
    if (dropPosition === 'after') {
      insertIndex = dropIndex + 1;
    }

    // Wenn der gezogene Tab vor dem Drop-Index war, müssen wir die Position anpassen
    if (draggedIndex < dropIndex) {
      insertIndex--;
    }

    newTabs.splice(insertIndex, 0, draggedTab);

    props.setOpenTabs(newTabs);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDropPosition('before');
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDropPosition('before');
  };

  const tap = (index: number, title: string, active: boolean, path: string) => {
    const showDropIndicator = dragOverIndex === index && draggedIndex !== null && draggedIndex !== index;
    // Special handling for README
    const displayTitle = title === 'readme' ? 'README.md' : `${title}.tsx`;

    return (
      <div
        key={index}
        className={`tapBar-tap ${active && 'tapBar-tapActive'} ${draggedIndex === index ? 'tapBar-tapDragging' : ''} ${showDropIndicator ? `tapBar-tapDropTarget tapBar-tapDropTarget-${dropPosition}` : ''}`}
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragEnter={(e) => handleDragEnter(e, index)}
        onDragOver={(e) => handleDragOver(e, index)}
        onDrop={(e) => handleDrop(e, index)}
        onDragEnd={handleDragEnd}
        onClick={(e) => {
          e.stopPropagation()
          openTab(path)
        }}
      >
        <GiTripleShells />
        {displayTitle}
        <Button
          disabled={props.openTabs.length === 1}
          icon={<VscChromeClose />}
          click={() => {closeTab(path)}}
          size='small'
        />
      </div>
    )
  }
  
  return (
    <div style={{ width: '100%', overflow: 'auto' }}>
      <div className='comp-tapBar' ref={tapBarRef}>
        {props.openTabs.map((tab, index) => tap(index, tab.title, location.pathname === tab.path, tab.path))}

        <div className='tapBar-right'>
          <Button
            icon={<VscEllipsis />}
            size='small'
            click={() => {}}
            dropdown={[{ title: t('tapbar.closeAll'), onClick: () => {
              props.setOpenTabs(props.openTabs.filter(tap => tap.path === '/'))
              navigate('/')
            }}]}
            dropdownPosition='rightEdge'
          />
        </div>
      </div>

      {props.openTabs.map((tab) => {
        if(location.pathname === tab.path) {
          const fileName = tab.title === 'readme' ? 'README.md' : `${tab.title}.tsx`;
          return (
            <div className='comp-breadcrumbs' key={tab.path + tab.title}>
              src <VscChevronRight /> pages <VscChevronRight /> {tab.title} <VscChevronRight /> <GiTripleShells  className='hole'/> {fileName}
            </div>
          )
        }
        return ''
      })}

    </div>
  )
}

export default Tapbar
